import { action, makeObservable, observable, reaction, runInAction } from 'mobx';
import { find, filter } from 'lodash';

import epaxios from '@/utils/epaxios';
import LogErrorMessage from '@/utils/LogErrorMessage';

import RootStore from '../RootStore';

import {
  mapToOptions,
  setDeviceTypesForModelSpec,
  setPayloadForCreateDevice,
} from '@/comp/MapBuilder/components/PIDEditing/PIDEditing.utils';
import { DeviceType } from '@/constants/deviceTypeEnum';
import { PipeLinkModel } from '@/comp/MapBuilder/links/PipeLink';
import { Entity } from '@/comp/MapBuilder/nodes/nodeWidget/NodeWidget.types';
import { Node } from '@/comp/MapBuilder/nodes/Node';
import { Device } from '../Station/constants';
import { permissions } from '@/constants';
import { DeviceKind, StationTier } from '@/constants/enums';
import {
  buildPhysicalSchema,
  DUMMY_MODEL_IDS,
  isDeviceKindDisconnected,
} from '@/comp/MapBuilder/nodes/nodeWidget/EditAssetDrawer/EditAssetDrawer.utils';
import { ModelSpec } from '@/store/SiteSurveyForm/constants';

const loadingDelay = 1000;

export enum MenuType {
  AddDevice = 'ADD_DEVICE',
  DeviceDetails = 'DEVICE_DETAILS',
  SensorActions = 'SENSOR_ACTIONS',
  CreateDevice = 'CREATE_DEVICE',
  None = 'NONE',
  DeleteDevice = 'DELETE_DEVICE',
  DeleteOnDevice = 'DELETE_ON_DEVICE',
  SaveChanges = 'SAVE_CHANGES',
  ConnectionPicker = 'CONNECTION_PICKER',
}

export interface MenuContext {
  deviceType?: DeviceType;
  entity?: Entity;
  parentEntity?: Entity;
  fromStationReadiness?: boolean;
}

export interface ActiveMenuState {
  type: MenuType;
  position: { x: number; y: number };
  context?: MenuContext;
}

export interface DeviceState {
  sourceDeviceId: string | string[];
  targetDeviceId: string | string[];
  name: string;
  deviceModelId: string;
  controllerId: string;
  deviceKind: DeviceKind;
}

type MapModelSpecSource = Pick<ModelSpec, 'id' | 'deviceKind' | 'technicalName' | 'deviceControllerId'>
type MapModelSpecTarget = {
  value: MapModelSpecSource['id']
  label: MapModelSpecSource['technicalName']
  controllerId: MapModelSpecSource['deviceControllerId']
  deviceKind: MapModelSpecSource['deviceKind']
}

export default class PIDEditStore {
  rootStore: typeof RootStore;

  @observable draftDevices = [];
  @observable draftDevicesWithoutPipes = [];
  @observable isEditModeEnabled = false;
  @observable activeMenu: ActiveMenuState | null = null;
  @observable isActiveMenuEnabled = false;
  @observable clickedPipeId: string | null = null;
  @observable previousClickedPipeId: string | null = null;
  @observable deviceState: DeviceState = {
    sourceDeviceId: '',
    targetDeviceId: '',
    name: '',
    deviceModelId: '',
    controllerId: '',
    deviceKind: null,
  };
  @observable isMultipleConnections = false;
  @observable connectedSourceDevices: Device[] = [];
  @observable isLoading = false;
  @observable isLoadingDraftPid = false;
  @observable selectedSourceDeviceIds: string[] = [];

  @observable isInEditMode = false;
  @observable draftDeviceChanged = false;
  @observable isLoadingDraft = false;
  @observable isLoadingModelSpec = false;
  @observable lastActivatedUserInfo = {
    lastEditor: '',
    activateTime: '',
  };

  @observable deviceControllers = [];
  @observable assetConnectionsInEditIds: string[] = [];
  @observable unusedSerialNumbers: string[] = [];
  @observable isLoadingUnusedSerialNumbers = false;

  private clearClickedPipeCallback: (() => void) | null = null;
  private deviceTypeChangeCallback: (() => void) | null = null;

  constructor(rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;

    const { stationStore } = this.rootStore;

    reaction(
      () => this.isInEditMode,
      async () => {
        if (this.isInEditMode) {
          await this.getEditDraft(stationStore.currentStation?.id);
          await this.getControllers();
        } else {
          await stationStore.getStationPID(stationStore.currentStation.id);
          await this.rootStore.devicesStore.getDevicesMetadata();
        }
      },
    );

    reaction(
      () => this.clickedPipeId,
      newPipeId => {
        if (newPipeId !== this.previousClickedPipeId) {
          this.hideMenu();
          this.previousClickedPipeId = newPipeId;
        }
      },
    );

    reaction(
      () => this.activeMenu?.context?.deviceType,
      () => {
        if (this.deviceTypeChangeCallback) {
          this.deviceTypeChangeCallback();
        }
      },
    );
  }

  @action setIsMultipleConnections = (isMultipleConnections: boolean) => {
    this.isMultipleConnections = isMultipleConnections;
  };

  getConnectedSourceDevices = (targetDeviceId: string, draftDevices: Device[]): Device[] => {
    const pipesToTargetDeviceIds = new Set(
      draftDevices.filter(d => d.toDevices?.includes(targetDeviceId)).map(d => d.deviceId),
    );

    if (pipesToTargetDeviceIds.size === 0) {
      return [];
    }

    const sourceDevices = draftDevices
      .filter(d => d.toDevices?.some(toId => pipesToTargetDeviceIds.has(toId)))
      .map(d => d);

    return sourceDevices;
  };

  @action setConnectedSourceDevices = (targetDeviceId: string) => {
    const { stationStore } = this.rootStore;
    const draftDevices = stationStore.currentStation.devices;
    this.connectedSourceDevices = this.getConnectedSourceDevices(targetDeviceId, draftDevices);
  };

  @action setSelectedSourceDeviceIds = (deviceIds: string[]) => {
    this.selectedSourceDeviceIds = deviceIds;
  };

  @action setSelectedTargetDeviceId = (pipeIds: PipeLinkModel[]) => {
    const targetDeviceIds = new Set<string>();
    for (const pipe of pipeIds) {
      const pipeTargetDevice = pipe.getTargetPort()?.getNode() as Node;
      const deviceId = pipeTargetDevice?.getEntity()?.deviceId;
      if (deviceId) {
        targetDeviceIds.add(deviceId);
      }
    }

    if (targetDeviceIds.size > 0) {
      this.setDeviceState({ targetDeviceId: [...targetDeviceIds] });
    }
  };

  @action setIsInEditMode = (isInEditMode: boolean) => {
    this.isInEditMode = isInEditMode;
  };

  @action getEditDraft = async id => {
    const { stationStore } = this.rootStore;
    const oldStation = find(stationStore.stations, { id });
    try {
      this.isLoadingDraft = true;
      const { data: response } = await epaxios.get(`/station/${id}/draftPid`);
      const newStation = { ...oldStation };
      newStation.devices = response.devices;
      stationStore.draftDevices = response.devices;
      this.draftDevicesWithoutPipes = response.devicesWithoutPipes;
      Object.assign(oldStation, newStation);
      await this.getUnusedSerialNumbers();
    } catch (e) {
      stationStore.assetsDevices = [];
      LogErrorMessage('Draft load error. Server status code ', e);
      stationStore.stationStoreUserError = true;
    } finally {
      setTimeout(() => {
        this.isLoadingDraft = false;
      }, loadingDelay);
    }
  };

  defaultCreateDeviceName = (deviceType: DeviceType, t: (key: string) => string) => {
    const { stationStore } = this.rootStore;
    const currentDeviceTypeDevices = stationStore.currentStation.devices.filter(
      device => device.deviceType === deviceType,
    );
    const deviceNames = currentDeviceTypeDevices.map(device => device.name);
    return `#${deviceNames.length + 1} ${t(deviceType)}`;
  };

  @action createDevice = async (deviceType: DeviceType) => {
    const { stationStore } = this.rootStore;

    const { name, sourceDeviceId, targetDeviceId, deviceModelId, controllerId } = this.deviceState;

    if (!stationStore.currentStation) {
      return;
    }

    const payload = setPayloadForCreateDevice(
      deviceType,
      name,
      stationStore.currentStation.customerId,
      stationStore.currentStation.siteId,
      stationStore.currentStation.id,
      sourceDeviceId,
      targetDeviceId,
      deviceModelId,
      controllerId,
      null
    );
    try {
      this.isLoadingDraft = true;
      await epaxios.post(`/station-mgmt/edit-draft/station/${stationStore.currentStation.id}/create-device`, payload);

      await this.getEditDraft(stationStore.currentStation.id);
      this.draftDeviceChanged = true;
    } catch (e) {
      console.log('error in createDevice', e);
      throw e.response?.data?.message || 'An unexpected error occurred while activating the draft.';
    } finally {
      this.setDeviceState({
        sourceDeviceId: '',
        targetDeviceId: '',
      });
      setTimeout(() => {
        this.isLoadingDraft = false;
      }, loadingDelay);
    }
  };

  @action resetDraft = async () => {
    const { stationStore } = this.rootStore;
    try {
      this.isLoading = true;
      await epaxios.post(`/station-mgmt/edit-draft/station/${stationStore.currentStation.id}/reset`);
      runInAction(() => {
        this.isInEditMode = false;
        this.draftDeviceChanged = false;
      });
    } catch (e) {
      console.error('Error resetting draft', e);
    } finally {
      setTimeout(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      }, loadingDelay + 800);
    }
  };

  @action resetDraftDeviceChanged = () => {
    this.draftDeviceChanged = false;
  };

  @action activateDraft = async () => {
    const { stationStore } = this.rootStore;
    try {
      this.isLoading = true;
      await epaxios.post(`/station-mgmt/edit-draft/station/${stationStore.currentStation.id}/activate-draft`);
      runInAction(() => {
        this.isInEditMode = false;
        this.draftDeviceChanged = false;
      });
    } catch (e) {
      throw e.response?.data?.message || 'An unexpected error occurred while activating the draft.';
    } finally {
      setTimeout(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      }, loadingDelay + 800);
    }
  };

  @action updateDevice = async (deviceId: string, device: Partial<Device>, refreshMap = true) => {
    const { stationStore } = this.rootStore;
    try {
      if (refreshMap) this.isLoadingDraft = true;
      await epaxios.put(
        `/station-mgmt/edit-draft/station/${stationStore.currentStation.id}/device/${deviceId}`,
        device,
      );

      if (refreshMap) {
        await this.getEditDraft(stationStore.currentStation.id);
        this.draftDeviceChanged = true;
      }
    } catch (e) {
      throw e.response.data;
    } finally {
      if (refreshMap)
        setTimeout(() => {
          this.isLoadingDraft = false;
        }, loadingDelay);
    }
  };

  async updateConnectedDevices(entity: Entity, values: { connectFrom: unknown }) {
    const draftDevices = this.rootStore.stationStore.currentStation.devices;

    const connectedSourceDevices = this.getConnectedSourceDevices(entity.deviceId, draftDevices);

    const getDeviceArrayDiff = (oldDevices: Device[], newDeviceIds: string[]) => {
      const oldIds = oldDevices.map(d => d.deviceId);

      const removed = oldDevices.filter(d => !newDeviceIds.includes(d.deviceId));
      const added = draftDevices.filter(d => newDeviceIds.includes(d.deviceId) && !oldIds.includes(d.deviceId));

      return { added, removed };
    };

    const { added, removed } = getDeviceArrayDiff(connectedSourceDevices, values.connectFrom);

    const addPromises = added.map(device =>
      this.updateDevice(
        device.deviceId,
        {
          physicalSchema: buildPhysicalSchema(device, {
            toDevices: [...this.getDraftDeviceWithoutPipesByIdToDevices(device.deviceId), entity.deviceId],
          }),
        },
        false,
      ),
    );

    const removePromises = removed.map(device => {
      return this.updateDevice(
        device.deviceId,
        {
          physicalSchema: buildPhysicalSchema(device, {
            toDevices: this.getDraftDeviceWithoutPipesByIdToDevices(device.deviceId).filter(
              id => id !== entity.deviceId,
            ),
          }),
        },
        false,
      );
    });

    await Promise.all([...addPromises, ...removePromises]);
  }

  @action lockStationDraft = async () => {
    const { user } = this.rootStore.userStore;
    const { stationStore } = this.rootStore;
    try {
      this.isLoading = true;
      const { data } = await epaxios.get(
        `/station-mgmt/edit-draft/station/${stationStore.currentStation.id}/current-draft-lock-acquirer`,
      );

      if (data.toLowerCase() !== user.email.toLowerCase()) {
        throw new Error(`${data} is editing this draft`);
      }
    } catch (error) {
      console.error('Error locking station draft', error);
      if (error.response) {
        throw error.response.data;
      }
      throw error;
    } finally {
      setTimeout(() => {
        this.isLoading = false;
      }, loadingDelay);
    }
  };

  @action disableEditModeLock = async (stationId?: string) => {
    if (!this.rootStore.permissionStore.isAuth(permissions.CAN_EDIT_MAP)) return;
    const { stationStore } = this.rootStore;
    try {
      this.isLoading = true;
      await epaxios.delete(
        `/station-mgmt/edit-draft/station/${stationId ?? stationStore.currentStation.id}/release-draft-lock`,
      );
      runInAction(() => {
        this.isInEditMode = false;
        this.draftDeviceChanged = false;
      });
    } catch (error) {
      console.error('Error disabling edit mode lock', error);
      throw error.response.data;
    } finally {
      setTimeout(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      }, loadingDelay);
    }
  };

  @action deleteDevice = async (device, refreshMap = true) => {
    const { stationStore } = this.rootStore;
    try {
      if (refreshMap) this.isLoadingDraft = true;
      await epaxios.delete(
        `/station-mgmt/edit-draft/station/${stationStore.currentStation.id}/delete-device/${device?.deviceId}`,
      );
      if (device?.onDevices?.length > 0) {
        await Promise.all(
          device?.onDevices?.map(onDeviceId =>
            epaxios.delete(
              `/station-mgmt/edit-draft/station/${stationStore.currentStation.id}/delete-device/${onDeviceId}`,
            ),
          ),
        );
      }

      if (refreshMap) {
        await this.getEditDraft(stationStore.currentStation.id);
        this.draftDeviceChanged = true;
      }
    } catch (error) {
      console.error('Error deleting device', error);
      throw error.response.data;
    } finally {
      if (refreshMap) {
        setTimeout(() => {
          this.isLoadingDraft = false;
        }, loadingDelay);
      }
    }
  };

  @action getModelSpec = async (deviceType: DeviceType) => {
    const { stationStore } = this.rootStore;
    try {
      this.isLoadingModelSpec = true;
      const deviceTypes = setDeviceTypesForModelSpec(deviceType);
      const { data } = await epaxios.get<ModelSpec[]>(`/station-mgmt/device-model/filter-contains`, {
        params: {
          subject: 'deviceTypes',
          value: deviceTypes,
        },
      });
      const isMonitorStation = stationStore.stationTier === StationTier.Monitor;
      return data
        .filter(model => !(isMonitorStation && model.deviceKind === DeviceKind.Controlled))
        .map(model => this.mapModelSpecToModelDetails(model));
    } catch (e) {
      LogErrorMessage('Model spec load error. Server status code ', e);
    } finally {
      this.isLoadingModelSpec = false;
    }
  };

  @action getControllers = async () => {
    try {
      this.isLoadingModelSpec = true;
      const { data } = await epaxios.get('/station-mgmt/edit-draft/device-controller');

      this.deviceControllers = mapToOptions(data);
    } catch (e) {
      LogErrorMessage('Controllers load error. Server status code ', e);
    } finally {
      this.isLoadingModelSpec = false;
    }
  };

  @action getDeviceModel = async (deviceModelId: string) => {
    try {
      this.isLoadingModelSpec = true;
      const { data } = await epaxios.get<ModelSpec>(`/station-mgmt/edit-draft/device-model/${deviceModelId}`);
      return this.mapModelSpecToModelDetails(data);
    } catch (e) {
      console.error('Error getting device model', e);
      throw e.response?.data ?? e;
    } finally {
      this.isLoadingModelSpec = false;
    }
  };

  @action getDeviceController = async (controllerId: string) => {
    if (controllerId == null || controllerId === '' || controllerId === 'no-controller') {
      return null;
    }
    try {
      this.isLoadingModelSpec = true;
      const { data } = await epaxios.get(`/station-mgmt/edit-draft/device-controller/${controllerId}`);
      return { id: data.id ?? controllerId, name: data.name };
    } catch (e) {
      console.error('Error getting device controller', e);
      throw e.response?.data ?? e;
    } finally {
      this.isLoadingModelSpec = false;
    }
  };

  @action getDeviceDetails = async (deviceId: string) => {
    const { stationStore } = this.rootStore;
    try {
      this.isLoadingModelSpec = true;
      const { data } = await epaxios.get(
        `/station-mgmt/edit-draft/station/${stationStore.currentStation.id}/draft-device/${deviceId}`,
      );
      const { controllerId, controllerName, deviceModelId, deviceModelName, attributes, deviceKind } = data.deviceDto;

      return {
        controllerId: isDeviceKindDisconnected(deviceKind)
          ? null
          : controllerId || this.rootStore.devicesStore.getDeviceControllerId(deviceId),
        controllerName,
        deviceModelId,
        deviceModelName,
        pipeDiameter: attributes?.pipeDiameter,
        pipeMaterial: attributes?.pipeMaterial,
        deviceKind,
      };
    } catch (e) {
      console.error('Error getting device details', e);
      throw e.response?.data ?? e;
    } finally {
      this.isLoadingModelSpec = false;
    }
  };

  setClearClickedPipeCallback(callback: () => void) {
    this.clearClickedPipeCallback = callback;
  }
  setDeviceTypeChangeCallback(callback: () => void) {
    this.deviceTypeChangeCallback = callback;
  }
  @action setClickedPipe = (pipe: PipeLinkModel | null) => {
    this.clickedPipeId = pipe?.getID() ?? null;
    if (pipe && this.isEditModeEnabled) {
      const pipeSourceDevice = pipe.getSourcePort()?.getNode() as Node;
      const pipeTargetDevice = pipe.getTargetPort()?.getNode() as Node;
      const sourceDeviceId = pipeSourceDevice?.getEntity()?.deviceId;
      const targetDeviceId = pipeTargetDevice?.getEntity()?.deviceId;
      if (this.isMultipleConnections) {
        this.setConnectedSourceDevices(targetDeviceId);
      } else {
        this.setDeviceState({
          sourceDeviceId: sourceDeviceId,
          targetDeviceId: targetDeviceId,
        });
      }
    } else {
      this.setDeviceState({
        sourceDeviceId: '',
        targetDeviceId: '',
      });
      this.selectedSourceDeviceIds = [];
    }
  };

  @action toggleEditMode = (val: boolean) => {
    this.isEditModeEnabled = val;

    if (!val) {
      this.clearEditModeState();
    }
  };

  @action clearEditModeState = () => {
    this.hideMenu();

    this.clickedPipeId = null;
    this.previousClickedPipeId = null;
    this.selectedSourceDeviceIds = [];

    if (this.clearClickedPipeCallback) {
      this.clearClickedPipeCallback();
    }
  };

  @action showMenu = (type: MenuType, position: { x: number; y: number }, context?: MenuContext) => {
    this.activeMenu = { type, position, context };
    this.isActiveMenuEnabled = true;
  };

  @action hideMenu = () => {
    this.activeMenu = { type: MenuType.None, position: this.activeMenu?.position, context: this.activeMenu?.context };
    this.isActiveMenuEnabled = false;
  };

  @action setDeviceState = (newState: Partial<PIDEditStore['deviceState']>) => {
    this.deviceState = { ...this.deviceState, ...newState };
  };

  @action addOnDevice = async (entity: Entity, onDevice) => {
    const { stationStore } = this.rootStore;
    const toDevices = this.getDraftDeviceWithoutPipesByIdToDevices(entity.deviceId);
    let createdDeviceId: string | null = null;

    try {
      this.isLoadingDraft = true;

      // Create the on-device
      const { data } = await epaxios.post(
        `/station-mgmt/edit-draft/station/${stationStore.currentStation.id}/create-on-device`,
        {
          ...onDevice,
          customerId: stationStore.currentStation.customerId,
          siteId: stationStore.currentStation.siteId,
          stationId: stationStore.currentStation.id,
        },
      );

      createdDeviceId = data?.id;

      // Update the device with the new on-device
      await this.updateDevice(
        entity.deviceId,
        {
          controllerId: entity.controllerId,
          physicalSchema: buildPhysicalSchema(entity, {
            toDevices: toDevices ?? [],
            onDevices: [...(entity.onDevices ?? []), createdDeviceId],
          }),
        },
        false,
      );
    } catch (error) {
      console.error('Error adding on device', error);

      // Rollback: delete the created device if update failed
      if (createdDeviceId) {
        try {
          await this.deleteDevice(createdDeviceId, false);
          console.log('Successfully rolled back device creation');
        } catch (rollbackError) {
          console.error('Error during rollback - created device may need manual cleanup:', rollbackError);
        }
      }

      throw error.message || 'An unexpected error occurred while adding on device.';
    } finally {
      setTimeout(() => {
        this.isLoadingDraft = false;
      }, loadingDelay);
    }
  };

  @action createDeviceWithConnections = async entity => {
    const { stationStore } = this.rootStore;

    try {
      this.isLoadingDraft = true;

      await epaxios.post(
        `/station-mgmt/edit-draft/station/${stationStore.currentStation.id}/create-device-with-connections`,
        {
          ...entity,
          customerId: stationStore.currentStation.customerId,
          siteId: stationStore.currentStation.siteId,
          stationId: stationStore.currentStation.id,
        },
      );

      await this.getEditDraft(stationStore.currentStation.id);
      this.draftDeviceChanged = true;
    } catch (error) {
      console.error('Error creating device with connections', error);

      throw error.response?.data || 'An unexpected error occurred while creating device with connections.';
    } finally {
      setTimeout(() => {
        this.isLoadingDraft = false;
      }, loadingDelay);
    }
  };

  getDraftDeviceWithoutPipesByIdToDevices = (deviceId: string) => {
    return find(this.draftDevicesWithoutPipes, device => device.deviceDto?.id === deviceId)?.deviceDto?.physicalSchema
      ?.toDevices;
  };

  getDraftDeviceWithoutPipesByIdFromDevices = (deviceId: string) => {
    return filter(this.draftDevicesWithoutPipes, device =>
      device.deviceDto?.physicalSchema?.toDevices?.includes(deviceId),
    ).map(device => device?.deviceDto?.id);
  };

  @action setAssetConnectionsInEditIds = (assetConnectionsInEditIds: string[]) => {
    this.assetConnectionsInEditIds = assetConnectionsInEditIds;
  };

  @action getUnusedSerialNumbers = async () => {
    this.isLoadingUnusedSerialNumbers = true;
    try {
      const { data } = await epaxios.get(
        `/salesforce/salesforce-assets/unused-serial-numbers/site/${this.rootStore.stationStore.currentStation.siteId}`,
      );

      this.unusedSerialNumbers = data;
    } catch (e) {
      this.unusedSerialNumbers = [];
      LogErrorMessage('Unused serial numbers load error. Server status code ', e);
    } finally {
      this.isLoadingUnusedSerialNumbers = false;
    }
  };

  @action disableEditMode = async () => {
    await this.disableEditModeLock();
    this.setIsInEditMode(false);
    this.toggleEditMode(false);
  };

  mapModelSpecToModelDetails(model: MapModelSpecSource): MapModelSpecTarget {
    return {
      value: model.id,
      label: Object.values(DUMMY_MODEL_IDS).includes(model.id as DUMMY_MODEL_IDS)
        ? 'No matching spec'
        : model.technicalName,
      controllerId: model.deviceControllerId,
      deviceKind: this.determineDeviceKind(model.deviceKind),
    };
  }

  private determineDeviceKind(deviceKindFromModel: DeviceKind) {
    const isMonitorStation = this.rootStore.stationStore.isMonitorStation;
    if (isMonitorStation && deviceKindFromModel === DeviceKind.Controlled) {
      return DeviceKind.Virtual;
    }
    return deviceKindFromModel;
  }
}
